import { useState, useEffect, useCallback } from 'react';
import supabase from '../supabase/supabase';

export function useTransactions(filters = {}) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payouts, setPayouts] = useState([]);

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('d2f_transactions')
        .select(`
          *,
          d2f_profiles!user_id (
            id,
            display_name,
            role
          ),
          d2f_drops!drop_id (
            id,
            title,
            cover_url
          ),
          d2f_artists!artist_id (
            id,
            name,
            slug
          ),
          d2f_subscription_tiers!subscription_tier_id (
            id,
            name,
            tier_name
          ),
          d2f_merch_items!merch_id (
            id,
            title,
            image_url
          )
        `)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters.status) {
        query = query.eq('status', filters.status);
      }

      if (filters.artistId) {
        query = query.eq('artist_id', filters.artistId);
      }

      if (filters.type) {
        // Filter by related table presence
        // This is handled client-side after fetch
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      // Client-side filtering by transaction type
      let filteredData = data || [];
      
      if (filters.type) {
        filteredData = filteredData.filter(tx => {
          if (filters.type === 'drop') return tx.drop_id !== null;
          if (filters.type === 'subscription') return tx.subscription_tier_id !== null;
          if (filters.type === 'merch') return tx.merch_id !== null;
          return true;
        });
      }

      // Enrich with email data (platform admin only)
      const profileIds = filteredData
        .map(tx => tx.d2f_profiles?.id)
        .filter(Boolean);

      setTransactions(filteredData);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchPayouts = useCallback(async () => {
    try {
      const { data, error: payoutsError } = await supabase
        .from('d2f_payouts')
        .select(`
          *,
          d2f_artists!artist_id (
            id,
            name,
            slug
          )
        `)
        .order('created_at', { ascending: false });

      if (payoutsError) throw payoutsError;
      setPayouts(data || []);
    } catch (err) {
      console.error('Error fetching payouts:', err);
    }
  }, []);

  const processRefund = useCallback(async (transactionId, reason) => {
    try {
      const { data, error: refundError } = await supabase.rpc('process_refund', {
        p_transaction_id: transactionId,
        p_reason: reason
      });

      if (refundError) throw refundError;
      
      // Refresh transactions after refund
      await fetchTransactions();
      
      return data;
    } catch (err) {
      console.error('Error processing refund:', err);
      throw err;
    }
  }, [fetchTransactions]);

  useEffect(() => {
    fetchTransactions();
    fetchPayouts();
  }, [fetchTransactions, fetchPayouts]);

  return {
    transactions,
    payouts,
    loading,
    error,
    refresh: fetchTransactions,
    processRefund
  };
}